import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, ArrowRight, Wallet, XCircle } from "lucide-react";

const POLL_INTERVAL_MS = 2000;
const POLL_MAX = 15;

export default function WalletReturn() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const nav = useNavigate();
  const [status, setStatus] = useState("loading");
  const [tries, setTries] = useState(0);
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("missing_session"); return; }
    let cancelled = false;
    let attempt = 0;

    const poll = async () => {
      if (cancelled) return;
      attempt += 1;
      setTries(attempt);
      try {
        const { data } = await api.get(`/marketplace/wallet/checkout/${sessionId}`);
        if (data.payment_status === "paid") {
          // Refresh balance from canonical endpoint
          try {
            const w = await api.get("/marketplace/wallet");
            setBalance(w.data.balance_cents);
          } catch {}
          setStatus("paid");
          return;
        }
        if (data.status === "expired" || (data.payment_status === "unpaid" && attempt > 3)) {
          setStatus("expired");
          return;
        }
      } catch {
        setStatus("error");
        return;
      }
      if (attempt >= POLL_MAX) { setStatus("timeout"); return; }
      setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <AppShell title="Wallet" subtitle="Top-up confirmation">
      <div className="max-w-xl">
        <div className="bg-white border border-slate-200 rounded-3xl p-8 text-center" data-testid="wallet-return-card">
          {status === "loading" && (
            <>
              <Loader2 className="w-10 h-10 mx-auto animate-spin text-slate-600" />
              <h2 className="font-display text-xl font-bold text-slate-900 mt-4">Confirming your payment…</h2>
              <p className="text-sm text-slate-500 mt-1">Hang tight — usually a few seconds. ({tries}/{POLL_MAX})</p>
            </>
          )}
          {status === "paid" && (
            <>
              <div className="w-14 h-14 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-3">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <h2 className="font-display text-2xl font-bold text-slate-900">Wallet topped up!</h2>
              {balance !== null && (
                <p className="text-sm text-slate-600 mt-2">New balance: <strong className="text-slate-900">${(balance / 100).toFixed(2)}</strong></p>
              )}
              <p className="text-sm text-slate-500 mt-2">You're set — we'll start routing matching leads to your dashboard.</p>
              <div className="mt-6 grid sm:grid-cols-2 gap-2">
                <Button onClick={() => nav("/dashboard")} className="rounded-xl bg-[#0F172A] hover:bg-black h-11" data-testid="wallet-return-dashboard">
                  Open dashboard <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
                <Button onClick={() => nav("/settings?tab=marketplace")} variant="outline" className="rounded-xl h-11">
                  Back to marketplace
                </Button>
              </div>
            </>
          )}
          {(status === "expired" || status === "timeout" || status === "error" || status === "missing_session") && (
            <>
              <div className="w-14 h-14 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto mb-3">
                <XCircle className="w-7 h-7" />
              </div>
              <h2 className="font-display text-xl font-bold text-slate-900">Top-up didn't complete</h2>
              <p className="text-sm text-slate-500 mt-1">
                {status === "expired" ? "The checkout session expired." :
                 status === "missing_session" ? "No session found in the URL." :
                 status === "timeout" ? "We're still waiting on Stripe — refresh in a minute." :
                 "Something went wrong. No funds were taken."}
              </p>
              <div className="mt-6">
                <Button onClick={() => nav("/settings?tab=marketplace")} className="rounded-xl bg-[#0F172A] hover:bg-black h-11">
                  <Wallet className="w-4 h-4 mr-1.5" /> Back to marketplace
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
}
